/**
 * New Relic utility functions for custom attributes and business-specific tracking
 * Focuses only on what New Relic doesn't track automatically
 */

declare global {
  interface Window {
    NREUM?: {
      setCustomAttribute?: (name: string, value: string | number | boolean) => void
      setCustomAttributes?: (attributes: Record<string, any>) => void
      addToActions?: (name: string, attributes?: Record<string, any>) => void
      noticeError?: (error: Error, customAttributes?: Record<string, any>) => void
    }
  }
}

/**
 * Check if New Relic is available
 */
export const isNewRelicAvailable = (): boolean => {
  return typeof window !== 'undefined' && !!window.NREUM
}

/**
 * Set custom attributes for the current session
 * Use this for user-specific data that New Relic doesn't track automatically
 */
export const setCustomAttributes = (attributes: Record<string, any>): void => {
  if (isNewRelicAvailable() && window.NREUM?.setCustomAttributes) {
    window.NREUM.setCustomAttributes(attributes)
  }
}

/**
 * Set a single custom attribute
 */
export const setCustomAttribute = (name: string, value: string | number | boolean): void => {
  if (isNewRelicAvailable() && window.NREUM?.setCustomAttribute) {
    window.NREUM.setCustomAttribute(name, value)
  }
}

/**
 * Track business-specific events that New Relic doesn't track automatically
 * Examples: user onboarding completion, feature usage, business workflows
 */
export const trackBusinessEvent = (eventName: string, attributes?: Record<string, any>): void => {
  if (isNewRelicAvailable() && window.NREUM?.addToActions) {
    window.NREUM.addToActions(eventName, attributes)
  }
  // Fallback to console in development
  if (process.env.NODE_ENV === 'development') {
    console.log('New Relic Business Event:', eventName, attributes)
  }
}

/**
 * Track custom errors with additional context
 * Use this for application-specific errors that need custom attributes
 */
export const trackCustomError = (error: Error, customAttributes?: Record<string, any>): void => {
  if (isNewRelicAvailable() && window.NREUM?.noticeError) {
    window.NREUM.noticeError(error, customAttributes)
  }
  // Fallback to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error('New Relic Custom Error:', error, customAttributes)
  }
}

// New Relic integration utility
export const initializeNewRelic = () => {
  // Only initialize in production
  if (import.meta.env.PROD && import.meta.env.VITE_NEW_RELIC_ENABLED === 'true') {
    // Create New Relic script element
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.innerHTML = `
      window.NREUM || (NREUM = {});
      NREUM.init = {
        distributed_tracing: { enabled: true },
        privacy: { cookies_enabled: true },
        ajax: { deny_list: ["bam.nr-data.net"] },
      };

      NREUM.loader_config = {
        accountID: "${import.meta.env.VITE_NEW_RELIC_ACCOUNT_ID || ''}",
        trustKey: "${import.meta.env.VITE_NEW_RELIC_TRUST_KEY || ''}",
        agentID: "${import.meta.env.VITE_NEW_RELIC_AGENT_ID || ''}",
        licenseKey: "${import.meta.env.VITE_NEW_RELIC_LICENSE_KEY || ''}",
        applicationID: "${import.meta.env.VITE_NEW_RELIC_APPLICATION_ID || ''}",
      };

      NREUM.info = {
        beacon: "bam.nr-data.net",
        errorBeacon: "bam.nr-data.net",
        licenseKey: "${import.meta.env.VITE_NEW_RELIC_LICENSE_KEY || ''}",
        applicationID: "${import.meta.env.VITE_NEW_RELIC_APPLICATION_ID || ''}",
        sa: 1,
      };
    `

    // Add the script to head
    document.head.appendChild(script)

    // Load New Relic loader
    const loaderScript = document.createElement('script')
    loaderScript.type = 'text/javascript'
    loaderScript.src = 'https://js-agent.newrelic.com/nr-loader-spa-1.266.0.min.js'
    document.head.appendChild(loaderScript)
  }
}

/**
 * Track user session attributes
 * Call this when user information becomes available
 */
export const setUserAttributes = (userInfo: {
  userId: string
  userRole?: string
  userEmail?: string
  userPermissions?: string[]
}): void => {
  const attributes: Record<string, any> = {
    userId: userInfo.userId,
    screenResolution: `${screen.width}x${screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    timestamp: Date.now(),
  }

  if (userInfo.userRole) attributes.userRole = userInfo.userRole
  if (userInfo.userEmail) attributes.userEmail = userInfo.userEmail
  if (userInfo.userPermissions) attributes.userPermissions = userInfo.userPermissions.join(',')

  setCustomAttributes(attributes)
}

/**
 * Track business-specific user actions
 * Use this for actions that have business value and aren't tracked by New Relic
 */
export const trackBusinessAction = (
  action: string,
  context: string,
  attributes?: Record<string, any>
): void => {
  trackBusinessEvent('BusinessAction', {
    action,
    context,
    timestamp: Date.now(),
    ...attributes,
  })
}

/**
 * Track feature usage
 * Use this to track which features users are using
 */
export const trackFeatureUsage = (
  featureName: string,
  action: string,
  attributes?: Record<string, any>
): void => {
  trackBusinessEvent('FeatureUsage', {
    featureName,
    action,
    timestamp: Date.now(),
    ...attributes,
  })
}
